import { useEffect, useState, useCallback, useMemo } from "react";
import { lang } from "../lang"; // Assuming lang.js is in ../lang

export function useTaskNavigation(tasks) {
  const [selectedPath, setSelectedPath] = useState(() => {
    // Initial state log
    if (process.env.NODE_ENV === "development") {
      console.log(
        "[useTaskNavigation] useState for selectedPath initialized to []."
      );
    }
    return [];
  });

  const stableTasks = useMemo(() => {
    if (process.env.NODE_ENV === "development") {
      // console.log("[useTaskNavigation] stableTasks re-memoized.");
    }
    return tasks;
  }, [tasks]);

  const findTaskInTree = useCallback((taskArray, id) => {
    for (const task of taskArray) {
      if (task.id === id) return task;
      if (task.children) {
        const found = findTaskInTree(task.children, id);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const selectTask = useCallback(
    (taskId, level) => {
      // This outer log happens when selectTask is CALLED.
      if (process.env.NODE_ENV === "development") {
        console.log(
          `-----> [selectTask CALLED] TaskId: ${taskId}, Level: ${level} <-----`
        );
      }

      setSelectedPath((prevPath) => {
        // This inner log happens when the setSelectedPath's updater function EXECUTES.
        if (process.env.NODE_ENV === "development") {
          console.groupCollapsed(
            `[selectTask SETTING_PATH] For TaskId: ${taskId}, Level: ${level}`
          );
          console.log(
            `Current prevPath inside updater: ${JSON.stringify(prevPath)}`
          );
          console.log(
            "stableTasks available to updater (first 3):",
            stableTasks.slice(0, 3).map((t) => ({ id: t.id, name: t.name }))
          );
        }

        const taskBeingConsidered = findTaskInTree(stableTasks, taskId);

        if (!taskBeingConsidered) {
          if (process.env.NODE_ENV === "development") {
            console.warn(
              `Task (taskId: ${taskId}) NOT FOUND in stableTasks. Returning prevPath.`
            );
            console.log("Returning path:", JSON.stringify(prevPath));
            console.groupEnd();
          }
          return prevPath;
        }

        let newPath;
        let decision = "";

        if (
          !taskBeingConsidered.children ||
          taskBeingConsidered.children.length === 0
        ) {
          decision = "LEAF_NODE";
          if (prevPath.length === level + 1 && prevPath[level] === taskId) {
            newPath = prevPath.slice(0, level);
            decision += " (Toggle UP from selected leaf)";
          } else {
            newPath = [...prevPath.slice(0, level), taskId];
            decision += " (Select/Extend to leaf)";
          }
        } else {
          decision = "PARENT_NODE";
          if (prevPath.length > level && prevPath[level] === taskId) {
            newPath = prevPath.slice(0, level);
            decision += " (Toggle UP from selected parent)";
          } else {
            newPath = [...prevPath.slice(0, level), taskId];
            decision += " (Navigate DOWN to parent)";
          }
        }

        if (process.env.NODE_ENV === "development") {
          console.log(`Decision for ${taskId}: ${decision}`);
          console.log(`Resulting newPath: ${JSON.stringify(newPath)}`);
          console.groupEnd();
        }
        return newPath;
      });
    },
    [stableTasks, findTaskInTree] // findTaskInTree is stable due to its own useCallback
  );

  const getColumns = useCallback(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[getColumns ENTRY] Recalculating. Current selectedPath state: ${JSON.stringify(
          selectedPath
        )}`
      );
    }

    let validPath = [];
    let currentTaskSetForPathValidation = stableTasks;

    for (let i = 0; i < selectedPath.length; i++) {
      const pathSegmentId = selectedPath[i];
      const taskInPath = findTaskInTree(
        currentTaskSetForPathValidation,
        pathSegmentId
      );
      if (!taskInPath) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `[getColumns] Invalid task ID in selectedPath at segment ${i}: ${pathSegmentId}. Path truncated.`
          );
        }
        break;
      }
      validPath.push(pathSegmentId);
      currentTaskSetForPathValidation = taskInPath.children || [];
    }

    const mainTasks = stableTasks.filter(
      (task) =>
        !stableTasks.some((parent) =>
          parent.children?.find((child) => child.id === task.id)
        )
    );
    const columnsToRender = [
      { title: lang.taskColumn.mainTasks, tasks: mainTasks },
    ];
    let currentTaskSetForColumnGen = stableTasks;

    for (const id of validPath) {
      const task = findTaskInTree(currentTaskSetForColumnGen, id);
      if (task && task.children && task.children.length > 0) {
        columnsToRender.push({ title: task.name, tasks: task.children });
        currentTaskSetForColumnGen = task.children;
      } else if (task) {
        currentTaskSetForColumnGen = [];
      } else {
        break;
      }
    }

    if (process.env.NODE_ENV === "development") {
      console.log(
        `[getColumns EXIT] Computed columns count: ${
          columnsToRender.length
        }. Validated path used: ${JSON.stringify(validPath)}`
      );
    }
    return columnsToRender;
  }, [stableTasks, selectedPath, findTaskInTree, lang.taskColumn.mainTasks]);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[useTaskNavigation EFFECT] selectedPath changed to: ${JSON.stringify(
          selectedPath
        )}`
      );
    }
  }, [selectedPath]);

  return {
    selectedPath,
    selectTask,
    getColumns,
  };
}
